import { defineStore, acceptHMRUpdate } from 'pinia'
import { ROLE_MATRIX } from '@/constants/acl'
import { useCustomers } from './customers'
import { getUser } from '../scripts/customers'

export const useUserRole = defineStore('userRole', {
    state: () => ({
        loading: true,
        user: null,
        userOrgRole: null,
        userRegRole: null,
    }),

    getters: {
        can() {
            return (resource, action, organizationId) => {
                if (this.loading || !resource || !action || organizationId == null) return false

                const rolesOnOrg = (this.userOrgRole || []).filter(
                    (o) => o.organization_id === organizationId && this.isActive(o.valid_from, o.valid_to),
                )
                if (!rolesOnOrg.length) return false

                return rolesOnOrg.some(({ role }) => {
                    const caps = ROLE_MATRIX[role]?.[resource] || ''
                    return caps.includes(action)
                })
            }
        },
        isNexatAdminInRegion() {
            return (regionId) => {
                const id = Number(regionId)
                if (this.loading || !Number.isFinite(id)) return false
                // Region id 0 means all regions
                // Check if given region id is in the list of regions and if the role is nexat_admin and the role is active
                return (this.userRegRole || []).some(
                    (r) => (Number(r.region_id) === 0 || Number(r.region_id) === id) &&
                        r.role === 'nexat_admin' &&
                        this.isActive(r.valid_from, r.valid_to)
                )
            }
        },
        hasNexatAdminInAnyRegion() {
            if (this.loading) return false

            return (this.userRegRole || []).some(
                (r) => r.role === 'nexat_admin' && this.isActive(r.valid_from, r.valid_to)
            )
        }
    },

    actions: {
        isActive(from, to, now = new Date()) {
            const vf = from ? new Date(from) : null
            const vt = to ? new Date(to) : null
            return (!vf || vf <= now) && (!vt || now < vt)
        },
        hasAnyAccess(resource, action) {
            const c = useCustomers()

            const list = c.selectedCustomers.length ? c.selectedCustomers : c.customers
            for (const o of list) {
                const id = Number(o.id)
                if (Number.isFinite(id) && this.can(resource, action, id)) {
                    return true
                }
            }
            return false
        },
        async getUserOrgRole(keycloakInstance) {
            const userId = keycloakInstance?.tokenParsed?.sub
            if (!userId) {
                console.error('Failed to get user ID from Keycloak token')
                return false
            }

            this.loading = true
            try {
                const user = await getUser(userId)
                this.user = user
                this.userOrgRole = user?.organizations || []
                const byRegionId = new Map()
                for (const organization of this.userOrgRole) {
                    const orgRegions = organization?.regions || []
                    for (const region of orgRegions) {
                        const regionId = region?.region_id ?? region?.id
                        if (regionId == null) {
                            continue
                        }

                        if (!byRegionId.has(regionId)) {
                            byRegionId.set(regionId, {
                                region_id: regionId,
                                role: region?.role ?? organization?.from_region_role ?? organization?.role,
                                valid_from: region?.valid_from ?? organization?.valid_from ?? null,
                                valid_to: region?.valid_to ?? organization?.valid_to ?? null
                            })
                        }
                    }
                }
                this.userRegRole = Array.from(byRegionId.values())
                this.loading = false

                return this.userOrgRole.some((o) => this.isActive(o.valid_from, o.valid_to))
            } catch (e) {
                console.error('Error loading user organization roles:', e)
                this.loading = false
                return false
            }
        },
        reset() {
            this.loading = false
            this.user = null
            this.userOrgRole = null
            this.userRegRole = null
        },
        getUserRoleByOrganizationId(organizationId) {
            return this.userOrgRole.find((o) => o.organization_id === organizationId && this.isActive(o.valid_from, o.valid_to))?.role
        },
        hasAnyNexatAdminAccess() {
            if (!this.userOrgRole || this.loading) return false

            const c = useCustomers()
            const list = c.selectedCustomers.length ? c.selectedCustomers : c.customers
            
            for (const o of list) {
                const id = Number(o.id)
                if (Number.isFinite(id)) {
                    const role = this.userOrgRole.find(
                        (r) => r.organization_id === id && r.role === 'nexat_admin' && this.isActive(r.valid_from, r.valid_to)
                    )
                    if (role) return true
                }
            }
            return false
        },
        isUserNexatAdminForOrgId(organizationId) {
            if (organizationId == null) return false
            const userRole = this.getUserRoleByOrganizationId(organizationId)
            return userRole === 'nexat_admin'
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserRole, import.meta.hot))
}
